<template>
  <div class="rp-root" data-aos="fade-right">
    <CrudToolbar title="Reporte Almacén" v-model="filtro" searchPlaceholder="Buscar">
      <template #extra>
        <CrudFechaRango v-model:fechaInicio="fechaInicio" v-model:fechaFin="fechaFin" />
      </template>
      <template #actions>
        <button
          class="btn-header btn-accion btn-exportar"
          type="button"
          :disabled="exportando"
          @click="exportar"
        >
          <span v-if="exportando" class="spinner-border spinner-border-sm"></span>
          <i v-else class="bi bi-file-earmark-excel-fill"></i>
          <span class="btn-header-texto ms-2">Exportar</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- ══ KPIs ══
         Propios de Almacén (distintos a los de Reporte de producción): el
         bloque debe dejar claro, a primera vista, qué reportó Taller, qué
         ya validó físicamente Almacén, qué sigue pendiente y si hay
         diferencias. Reactivos: si hay un reporte seleccionado, calculan
         solo sobre sus líneas; si no, sobre todos los reportes listados
         (ya filtrados por el rango de fechas de emisión). -->
    <div class="rp-kpis">
      <div class="rp-kpi rp-kpi--verde-suave">
        <span class="rp-kpi-icono"><i class="bi bi-clipboard-data-fill"></i></span>
        <div class="rp-kpi-texto">
          <span class="rp-kpi-label">Producción reportada</span>
          <span class="rp-kpi-valor"
            >{{ formatNum(kpisAlmacen.reportado) }}
            <span class="rp-kpi-unidad">pzas</span></span
          >
          <span class="rp-kpi-sub">
            {{
              reporteSeleccionado
                ? `${kpisAlmacen.total} ${
                    kpisAlmacen.total === 1 ? "registro" : "registros"
                  }`
                : `${reportes.length} ${reportes.length === 1 ? "reporte" : "reportes"}`
            }}
          </span>
        </div>
      </div>
      <div class="rp-kpi rp-kpi--verde">
        <span class="rp-kpi-icono"><i class="bi bi-check2-square"></i></span>
        <div class="rp-kpi-texto">
          <span class="rp-kpi-label">Producción recibida</span>
          <span class="rp-kpi-valor"
            >{{ formatNum(kpisAlmacen.recibido) }}
            <span class="rp-kpi-unidad">pzas</span></span
          >
          <span class="rp-kpi-sub">{{ kpisAlmacen.revisadas }} registros validados</span>
        </div>
      </div>
      <div class="rp-kpi rp-kpi--amarillo">
        <span class="rp-kpi-icono"><i class="bi bi-hourglass-split"></i></span>
        <div class="rp-kpi-texto">
          <span class="rp-kpi-label">Líneas pendientes</span>
          <span class="rp-kpi-valor">{{ kpisAlmacen.pendientes }}</span>
          <span class="rp-kpi-sub">por recibir</span>
        </div>
      </div>
      <div class="rp-kpi rp-kpi--naranja">
        <span class="rp-kpi-icono"><i class="bi bi-exclamation-triangle-fill"></i></span>
        <div class="rp-kpi-texto">
          <span class="rp-kpi-label">Con diferencias</span>
          <span class="rp-kpi-valor">{{ kpiDiferencias.total }}</span>
          <span class="rp-kpi-sub">{{ subtituloDiferencias }}</span>
        </div>
      </div>
      <div class="rp-kpi rp-kpi--azul">
        <span class="rp-kpi-icono"><i class="bi bi-pie-chart-fill"></i></span>
        <div class="rp-kpi-texto">
          <span class="rp-kpi-label">% recepción</span>
          <span class="rp-kpi-valor">{{ kpisAlmacen.porcentaje }}%</span>
          <span class="rp-kpi-sub">avance de revisión</span>
        </div>
      </div>
    </div>

    <!-- ══ TABS DE ESTATUS ══ -->
    <div class="rp-tabs">
      <button
        v-for="t in TABS"
        :key="t.valor"
        class="rp-tab"
        :class="['rp-tab--' + t.valor.toLowerCase(), { activo: tabActivo === t.valor }]"
        @click="cambiarTab(t.valor)"
      >
        <span class="rp-tab-icono" :class="'rp-tab-icono--' + t.valor.toLowerCase()">
          <i class="bi" :class="t.icono"></i>
        </span>
        <span class="rp-tab-label">{{ t.label }}</span>
        <span class="rp-tab-count">({{ conteos[t.valor] ?? 0 }})</span>
      </button>
    </div>

    <!-- ══ TABLA ══ -->
    <div class="divisan-table-container shadow-lg">
      <div v-if="cargando" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>

      <!-- ── Vista detalle (reporte seleccionado) ── -->
      <template v-else-if="reporteSeleccionado">
        <div class="rp-detalle-header">
          <button
            class="btn-regresar"
            type="button"
            title="Regresar"
            aria-label="Regresar"
            @click="reporteSeleccionadoId = null"
          >
            <i class="bi bi-arrow-left"></i>
          </button>
          <div class="rp-detalle-info">
            <strong class="rp-fecha-larga">{{
              formatFecha(reporteSeleccionado.fecha)
            }}</strong>
            <strong class="rp-fecha-corta">{{
              formatFechaCorta(reporteSeleccionado.fecha)
            }}</strong>
            <span class="rp-detalle-encargado">
              <span class="rp-detalle-sep">·</span>
              <span>{{ reporteSeleccionado.encargado_nombre }}</span>
            </span>
            <span
              :class="['badge-estatus-rp', claseEstatus(reporteSeleccionado.estatus)]"
            >
              <span class="rp-estatus-largo">{{
                etiquetaEstatus(reporteSeleccionado.estatus)
              }}</span>
              <span class="rp-estatus-corto">{{
                etiquetaEstatusCorta(reporteSeleccionado.estatus)
              }}</span>
            </span>
          </div>
          <button
            v-if="reporteSeleccionado.estatus === 'ENVIADO'"
            class="btn-confirmar-recepcion"
            type="button"
            :disabled="!puedeConfirmarRecepcion || confirmandoRecepcion"
            :title="
              puedeConfirmarRecepcion
                ? 'Confirmar recepción'
                : 'Captura el conteo de Almacén en todos los renglones'
            "
            @click="confirmarRecepcion"
          >
            <span
              v-if="confirmandoRecepcion"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            <i v-else class="bi bi-check2-circle me-2"></i>
            Confirmar recepción
          </button>
        </div>
        <div class="table-scroll rp-vista-desktop">
          <table class="table table-divisan align-middle mb-0">
            <thead>
              <tr>
                <th
                  class="ps-4 th-ordenable"
                  style="width: 90px"
                  @click="ordenarLineasPor('maquina_nombre')"
                >
                  Máquina
                  <i
                    class="bi ms-1"
                    :class="
                      sortKeyLineas === 'maquina_nombre'
                        ? sortDirLineas === 'asc'
                          ? 'bi-arrow-up'
                          : 'bi-arrow-down'
                        : 'bi-arrow-down-up th-ordenable-hint'
                    "
                  ></i>
                </th>
                <th
                  class="rp-th-wrap th-ordenable"
                  style="width: 160px"
                  @click="ordenarLineasPor('operador_nombre')"
                >
                  Operador
                  <i
                    class="bi ms-1"
                    :class="
                      sortKeyLineas === 'operador_nombre'
                        ? sortDirLineas === 'asc'
                          ? 'bi-arrow-up'
                          : 'bi-arrow-down'
                        : 'bi-arrow-down-up th-ordenable-hint'
                    "
                  ></i>
                </th>
                <th
                  class="th-ordenable"
                  style="min-width: 190px"
                  @click="ordenarLineasPor('producto_nombre')"
                >
                  Producto / Modelo
                  <i
                    class="bi ms-1"
                    :class="
                      sortKeyLineas === 'producto_nombre'
                        ? sortDirLineas === 'asc'
                          ? 'bi-arrow-up'
                          : 'bi-arrow-down'
                        : 'bi-arrow-down-up th-ordenable-hint'
                    "
                  ></i>
                </th>
                <th
                  class="text-center th-ordenable"
                  style="width: 95px"
                  @click="ordenarLineasPor('cantidad_producida')"
                >
                  Cantidad
                  <i
                    class="bi ms-1"
                    :class="
                      sortKeyLineas === 'cantidad_producida'
                        ? sortDirLineas === 'asc'
                          ? 'bi-arrow-up'
                          : 'bi-arrow-down'
                        : 'bi-arrow-down-up th-ordenable-hint'
                    "
                  ></i>
                </th>
                <th
                  class="text-center th-ordenable"
                  style="width: 85px"
                  @click="ordenarLineasPor('scrap')"
                >
                  Scrap
                  <i
                    class="bi ms-1"
                    :class="
                      sortKeyLineas === 'scrap'
                        ? sortDirLineas === 'asc'
                          ? 'bi-arrow-up'
                          : 'bi-arrow-down'
                        : 'bi-arrow-down-up th-ordenable-hint'
                    "
                  ></i>
                </th>
                <th
                  class="text-center th-ordenable"
                  style="width: 85px"
                  @click="ordenarLineasPor('cantidad_neta')"
                >
                  Neto
                  <i
                    class="bi ms-1"
                    :class="
                      sortKeyLineas === 'cantidad_neta'
                        ? sortDirLineas === 'asc'
                          ? 'bi-arrow-up'
                          : 'bi-arrow-down'
                        : 'bi-arrow-down-up th-ordenable-hint'
                    "
                  ></i>
                </th>
                <th class="text-center" style="width: 115px">Almacén</th>
                <th class="text-center" style="width: 95px">Diferencia</th>
                <th class="rp-th-wrap" style="width: 130px">Estatus</th>
                <th class="text-center" style="width: 70px">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="l in lineasPaginadas" :key="l.id">
                <td class="ps-4">{{ l.maquina_nombre }}</td>
                <td class="rp-celda-wrap">{{ l.operador_nombre }}</td>
                <td>
                  <span class="fw-bold text-cafe">{{ l.producto_nombre }}</span>
                  <div v-if="l.producto_modelo || l.color_hex" class="rp-prod-modelo-row">
                    <small v-if="l.producto_modelo" class="text-verde opacity-75">{{
                      l.producto_modelo
                    }}</small>
                    <span
                      v-if="l.color_hex"
                      class="rp-color-swatch-rect"
                      :style="{ backgroundColor: l.color_hex }"
                      :title="l.color_nombre || ''"
                    ></span>
                  </div>
                </td>
                <td class="text-center">{{ formatNum(l.cantidad_producida) }}</td>
                <td class="text-center">{{ formatNum(l.scrap) }}</td>
                <td class="text-center fw-bold">{{ formatNum(l.cantidad_neta) }}</td>
                <td class="text-center">
                  <input
                    v-model.number="l.cantidad_recibida"
                    type="number"
                    min="0"
                    class="form-control form-control-sm ra-input-almacen"
                    :class="{ guardando: guardandoRecibidoId === l.id }"
                    placeholder="—"
                    :disabled="
                      reporteSeleccionado.estatus !== 'ENVIADO' ||
                      l.estatus_validacion === 'DIFERENCIA_JUSTIFICADA'
                    "
                    @change="guardarRecibido(l)"
                  />
                </td>
                <td class="text-center">
                  <span v-if="diferenciaLinea(l) === null" class="ra-diferencia-vacia"
                    >—</span
                  >
                  <span
                    v-else
                    :class="['ra-diferencia', claseDiferencia(diferenciaLinea(l))]"
                  >
                    {{ diferenciaLinea(l) > 0 ? "+" : ""
                    }}{{ formatNum(diferenciaLinea(l)) }}
                  </span>
                </td>
                <td class="rp-celda-wrap">
                  <span :class="['ra-badge-validacion', claseValidacion(l)]">
                    {{ etiquetaValidacion(l) }}
                  </span>
                </td>
                <td class="text-center">
                  <button
                    v-if="l.estatus_validacion === 'CON_DIFERENCIA'"
                    type="button"
                    class="btn-accion-diferencia"
                    title="Registrar diferencia de recepción"
                    @click="abrirModalDiferencia(l)"
                  >
                    <i class="bi bi-exclamation-triangle-fill"></i>
                  </button>
                  <button
                    v-else-if="l.estatus_validacion === 'DIFERENCIA_JUSTIFICADA'"
                    type="button"
                    class="btn-accion-diferencia btn-accion-diferencia--bloqueado"
                    disabled
                    title="Diferencia ya justificada"
                  >
                    <i class="bi bi-lock-fill"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ── Cards móvil (detalle) ── -->
        <div class="rp-vista-movil">
          <div
            v-for="l in lineasPaginadas"
            :key="'mob-' + l.id"
            class="rp-card"
            :class="claseEstatus(reporteSeleccionado.estatus)"
          >
            <div class="rp-linea-card-top">
              <span class="fw-bold text-cafe flex-grow-1">{{ l.producto_nombre }}</span>
              <span :class="['badge-aditivo-rp', { activo: l.aditivo }]">{{
                l.aditivo ? "C/A" : "S/A"
              }}</span>
            </div>
            <small v-if="l.producto_modelo" class="d-block text-verde opacity-75">{{
              l.producto_modelo
            }}</small>
            <div class="rp-linea-card-info">
              <span><i class="bi bi-gear-fill me-1"></i>{{ l.maquina_nombre }}</span>
              <span><i class="bi bi-person-fill me-1"></i>{{ l.operador_nombre }}</span>
              <span>
                <span
                  v-if="l.color_hex"
                  class="rp-color-swatch"
                  :style="{ backgroundColor: l.color_hex }"
                ></span>
                {{ l.color_nombre || "—" }}
              </span>
            </div>
            <div class="rp-card-stats rp-linea-card-stats">
              <div>
                <small>Cantidad</small
                ><strong>{{ formatNum(l.cantidad_producida) }}</strong>
              </div>
              <div>
                <small>Scrap</small><strong>{{ formatNum(l.scrap) }}</strong>
              </div>
              <div>
                <small>Neto</small><strong>{{ formatNum(l.cantidad_neta) }}</strong>
              </div>
            </div>
            <div class="ra-linea-card-estatus">
              <span :class="['ra-badge-validacion', claseValidacion(l)]">
                {{ etiquetaValidacion(l) }}
              </span>
              <button
                v-if="l.estatus_validacion === 'CON_DIFERENCIA'"
                type="button"
                class="btn-accion-diferencia"
                title="Registrar diferencia de recepción"
                @click="abrirModalDiferencia(l)"
              >
                <i class="bi bi-exclamation-triangle-fill"></i>
              </button>
              <button
                v-else-if="l.estatus_validacion === 'DIFERENCIA_JUSTIFICADA'"
                type="button"
                class="btn-accion-diferencia btn-accion-diferencia--bloqueado"
                disabled
                title="Diferencia ya justificada"
              >
                <i class="bi bi-lock-fill"></i>
              </button>
            </div>
            <div class="ra-linea-card-validacion">
              <div class="ra-linea-card-almacen">
                <label>Almacén</label>
                <input
                  v-model.number="l.cantidad_recibida"
                  type="number"
                  min="0"
                  class="form-control form-control-sm ra-input-almacen"
                  :class="{ guardando: guardandoRecibidoId === l.id }"
                  placeholder="—"
                  :disabled="
                    reporteSeleccionado.estatus !== 'ENVIADO' ||
                    l.estatus_validacion === 'DIFERENCIA_JUSTIFICADA'
                  "
                  @change="guardarRecibido(l)"
                />
              </div>
              <div class="ra-linea-card-diferencia">
                <label>Diferencia</label>
                <span v-if="diferenciaLinea(l) === null" class="ra-diferencia-vacia"
                  >—</span
                >
                <span
                  v-else
                  :class="['ra-diferencia', claseDiferencia(diferenciaLinea(l))]"
                >
                  {{ diferenciaLinea(l) > 0 ? "+" : ""
                  }}{{ formatNum(diferenciaLinea(l)) }}
                </span>
              </div>
            </div>
          </div>
          <p
            v-if="lineasPaginadas.length === 0"
            class="text-center py-5 text-cafe opacity-50 fw-bold"
          >
            No se encontraron renglones
          </p>
        </div>

        <!-- Paginación de la tabla de detalle -->
        <div class="paginacion-wrapper">
          <div class="pag-izq">
            <small class="pag-info"
              >{{ rangoInicioLineas }}–{{ rangoFinLineas }} de
              {{ lineasOrdenadas.length }} renglones</small
            >
            <div class="por-pagina-wrap">
              <label class="por-pagina-label">Filas:</label>
              <select
                class="por-pagina-select"
                :value="porPaginaLineas"
                @change="cambiarPorPaginaLineas(Number($event.target.value))"
              >
                <option
                  v-for="n in paginacionLineasStore.opcionesPorPagina"
                  :key="n"
                  :value="n"
                >
                  {{ n }}
                </option>
              </select>
            </div>
          </div>
          <div class="pag-der">
            <button
              class="btn-pag"
              :disabled="paginaLineas === 1"
              @click="paginaLineas = 1"
              title="Primera"
            >
              <i class="bi bi-chevron-double-left"></i>
            </button>
            <button
              class="btn-pag"
              :disabled="paginaLineas === 1"
              @click="paginaLineas--"
            >
              <i class="bi bi-chevron-left"></i>
            </button>
            <template v-for="p in paginasVisiblesLineas" :key="p">
              <span v-if="p === '...'" class="pag-ellipsis">…</span>
              <button
                v-else
                :class="['btn-pag-num', { activo: p === paginaLineas }]"
                @click="paginaLineas = p"
              >
                {{ p }}
              </button>
            </template>
            <button
              class="btn-pag"
              :disabled="paginaLineas === totalPaginasLineas"
              @click="paginaLineas++"
            >
              <i class="bi bi-chevron-right"></i>
            </button>
            <button
              class="btn-pag"
              :disabled="paginaLineas === totalPaginasLineas"
              @click="paginaLineas = totalPaginasLineas"
              title="Última"
            >
              <i class="bi bi-chevron-double-right"></i>
            </button>
          </div>
        </div>
      </template>

      <!-- ── Vista resumen (lista de reportes) ── -->
      <template v-else>
        <div class="table-scroll rp-vista-desktop">
          <table class="table table-divisan align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-4">No</th>
                <th>Fecha</th>
                <th>Encargado</th>
                <th class="text-center">Productos</th>
                <th class="text-center">Colores</th>
                <th class="text-center">Producción</th>
                <th class="text-center">Scrap</th>
                <th>Estatus</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(r, idx) in reportes"
                :key="r.id"
                class="rp-fila-clicable"
                @click="reporteSeleccionadoId = r.id"
              >
                <td class="ps-4">{{ idx + 1 }}</td>
                <td>{{ formatFecha(r.fecha) }}</td>
                <td class="fw-bold text-cafe">{{ r.encargado_nombre }}</td>
                <td class="text-center">{{ r.total_productos }}</td>
                <td class="text-center">{{ r.total_colores }}</td>
                <td class="text-center">{{ formatNum(r.total_cantidad_producida) }}</td>
                <td class="text-center">{{ formatNum(r.total_scrap) }}</td>
                <td>
                  <span :class="['badge-estatus-rp', claseEstatus(r.estatus)]">{{
                    etiquetaEstatus(r.estatus)
                  }}</span>
                </td>
              </tr>
              <tr v-if="reportes.length === 0">
                <td colspan="8" class="text-center py-5 text-cafe opacity-50 fw-bold">
                  No se encontraron reportes
                </td>
              </tr>
            </tbody>
            <tfoot v-if="reportes.length">
              <tr class="rp-fila-totales">
                <td colspan="3" class="ps-4">
                  Mostrando {{ reportes.length }} registros
                </td>
                <td></td>
                <td></td>
                <td class="text-center">{{ formatNum(kpis.total_producido) }}</td>
                <td class="text-center">{{ formatNum(kpis.scrap_total) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- ── Cards móvil ── -->
        <div class="rp-vista-movil">
          <div
            v-for="(r, idx) in reportes"
            :key="'mob-' + r.id"
            class="rp-card"
            :class="claseEstatus(r.estatus)"
            @click="reporteSeleccionadoId = r.id"
          >
            <span class="rp-card-no">#{{ idx + 1 }}</span>
            <div class="rp-card-top">
              <span class="fw-bold text-cafe flex-grow-1">{{ r.encargado_nombre }}</span>
            </div>
            <div class="rp-card-meta">
              <span class="rp-card-fecha">{{ formatFecha(r.fecha) }}</span>
              <span :class="['badge-estatus-rp', claseEstatus(r.estatus)]">{{
                etiquetaEstatus(r.estatus)
              }}</span>
            </div>
            <div class="rp-card-stats">
              <div>
                <small>Productos</small><strong>{{ r.total_productos }}</strong>
              </div>
              <div>
                <small>Colores</small><strong>{{ r.total_colores }}</strong>
              </div>
              <div>
                <small>Producción</small
                ><strong>{{ formatNum(r.total_cantidad_producida) }}</strong>
              </div>
              <div>
                <small>Scrap</small><strong>{{ formatNum(r.total_scrap) }}</strong>
              </div>
            </div>
          </div>
          <p
            v-if="reportes.length === 0"
            class="text-center py-5 text-cafe opacity-50 fw-bold"
          >
            No se encontraron reportes
          </p>
        </div>
      </template>
    </div>

    <!-- ══ MODAL: Registrar diferencia de recepción ══ -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalDiferenciaElement">
      <div class="modal-dialog modal-dialog-centered rd-modal-dialog">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              <i class="bi bi-exclamation-triangle-fill me-2 text-verde"></i>Registrar
              diferencia de recepción
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Cerrar"
            ></button>
          </div>
          <div v-if="lineaDiferenciaActual" class="modal-body">
            <p class="text-cafe opacity-80 rd-subtitulo">
              Indica el motivo de la diferencia antes de continuar.
            </p>
            <div class="rd-info-stack">
              <div class="rd-info-box rd-info-row--2">
                <div>
                  <small>Producto / Modelo</small>
                  <strong>{{ lineaDiferenciaActual.producto_nombre }}</strong>
                </div>
                <div>
                  <small>Operador</small>
                  <strong>{{ lineaDiferenciaActual.operador_nombre }}</strong>
                </div>
              </div>
              <div class="rd-info-box rd-info-row--3">
                <div>
                  <small>Fecha producción</small>
                  <strong>{{ formatFecha(reporteSeleccionado.fecha) }}</strong>
                </div>
                <div>
                  <small>Máquina</small>
                  <strong>{{ lineaDiferenciaActual.maquina_nombre }}</strong>
                </div>
                <div>
                  <small>Color</small>
                  <strong>{{ lineaDiferenciaActual.color_nombre || "—" }}</strong>
                </div>
              </div>
              <div class="rd-info-box rd-info-row--3">
                <div>
                  <small>Reportado por Taller</small>
                  <strong>{{ formatNum(lineaDiferenciaActual.cantidad_neta) }} pzas</strong>
                </div>
                <div>
                  <small>Recibido Almacén</small>
                  <strong
                    >{{ formatNum(lineaDiferenciaActual.cantidad_recibida) }} pzas</strong
                  >
                </div>
                <div>
                  <small>Diferencia</small>
                  <strong
                    :class="[
                      'rd-diferencia-valor',
                      claseDiferencia(lineaDiferenciaActual.diferencia),
                    ]"
                  >
                    {{ lineaDiferenciaActual.diferencia > 0 ? "+" : ""
                    }}{{ formatNum(lineaDiferenciaActual.diferencia) }}
                    pzas
                  </strong>
                </div>
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-12 col-md-6">
                <label class="form-label label-divisan small"
                  >Motivo de diferencia <span class="text-danger">*</span></label
                >
                <SelectDropdown
                  v-model="formDiferencia.motivo"
                  :options="MOTIVOS_DIFERENCIA_RECEPCION"
                  :optionValue="(m) => m"
                  :optionLabel="(m) => m"
                  placeholder="Selecciona un motivo"
                />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label label-divisan small"
                  >Observaciones
                  <span v-if="observacionesRequeridas" class="text-danger">*</span>
                </label>
                <textarea
                  v-model="formDiferencia.observaciones"
                  class="form-control divisan-input-modal rd-textarea-observaciones"
                  rows="1"
                  maxlength="250"
                  :placeholder="
                    observacionesRequeridas
                      ? 'Obligatorio: explica la diferencia'
                      : 'Opcional'
                  "
                ></textarea>
                <small class="rd-contador"
                  >{{ formDiferencia.observaciones.length }}/250</small
                >
              </div>
            </div>

            <p class="rd-nota">
              <i class="bi bi-info-circle-fill"></i>
              <span
                >La cantidad reportada por Taller no puede modificarse desde
                Almacén.</span
              >
            </p>
          </div>
          <div class="modal-footer border-0 pt-0 d-flex gap-2">
            <button
              type="button"
              class="btn-cancelar-ajuste"
              data-bs-dismiss="modal"
              :disabled="guardandoDiferencia"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-nuevo flex-fill py-2"
              :disabled="guardandoDiferencia"
              @click="guardarDiferencia"
            >
              <span
                v-if="guardandoDiferencia"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              <i v-else class="bi bi-check-lg me-1"></i>
              Aceptar diferencia
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { usePaginacionLineasReporteProduccionStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import CrudFechaRango from "@/components/Shared/CrudFechaRango.vue";
import SelectDropdown from "@/components/Shared/SelectDropdown.vue";
import {
  getReportesProduccion,
  getResumenReportesProduccion,
  exportarReportesProduccion,
  capturarCantidadRecibida,
  justificarDiferenciaLinea,
  confirmarRecepcionReporte,
} from "@/utils/api_backend";

// Catálogo fijo de motivos de diferencia (RN "Manejo de diferencias en
// recepción de producción"): no cambia seguido, así que vive aquí en vez
// de pedirse al backend. Debe coincidir exactamente con
// MotivoDiferenciaRecepcion.CHOICES en apps/generics/enums.py.
const MOTIVOS_DIFERENCIA_RECEPCION = [
  "Entrega incompleta",
  "Sobrante en entrega",
  "Faltante físico",
  "Error de captura en Taller",
  "Error de conteo",
  "Producto retenido para retrabajo",
  "Producto/color incorrecto",
  "Otro",
];
const MOTIVOS_REQUIEREN_OBSERVACIONES = new Set([
  "Otro",
  "Error de captura en Taller",
  "Faltante físico",
  "Producto retenido para retrabajo",
]);

// Almacén solo valida lo que Taller (Nico) ya envió: "Por validar" es la
// pestaña de trabajo activo, "Recepción confirmada" es su historial. Los
// reportes PENDIENTE (aún no enviados por Taller) o CANCELADO no son su
// responsabilidad aquí.
const TABS = [
  { valor: "ENVIADO", label: "Por validar", icono: "bi-send" },
  { valor: "COMPLETADO", label: "Recepción confirmada", icono: "bi-check-circle" },
];
function claseEstatus(v) {
  return "badge-estatus-rp--" + v.toLowerCase();
}

// Etiquetas propias de esta pantalla en vez del estatus_display que manda
// el backend ("Enviados a almacén") — desde la perspectiva de Almacén el
// reporte ya "está en almacén", no que se esté "enviando".
const ESTATUS_LARGO = {
  ENVIADO: "En almacén",
  COMPLETADO: "Recepción completada",
};
const ESTATUS_CORTO = {
  ENVIADO: "En almacén",
  COMPLETADO: "Completado",
};
function etiquetaEstatus(v) {
  return ESTATUS_LARGO[v] || v;
}
function etiquetaEstatusCorta(v) {
  return ESTATUS_CORTO[v] || v;
}

// ── Filtros / estado de la tabla ─────────────────────────────
const filtro = ref("");
const fechaInicio = ref("");
const fechaFin = ref("");
const tabActivo = ref("ENVIADO");
const cargando = ref(false);
const reportes = ref([]);
const kpis = ref({});
const conteos = ref({ PENDIENTE: 0, ENVIADO: 0, COMPLETADO: 0, CANCELADO: 0 });
const reporteSeleccionadoId = ref(null);
const exportando = ref(false);

const reporteSeleccionado = computed(() =>
  reportes.value.find((r) => r.id === reporteSeleccionadoId.value)
);

// ── Ordenamiento y paginación de la tabla de detalle (líneas) ──
// Vive aparte de la paginación/orden de la tabla resumen de arriba, con su
// propio store, para que un ajuste acá no afecte a la otra tabla.
const paginacionLineasStore = usePaginacionLineasReporteProduccionStore();
const sortKeyLineas = ref(null);
const sortDirLineas = ref("asc");
const paginaLineas = ref(1);
const CAMPOS_NUMERICOS_LINEAS = new Set(["cantidad_producida", "scrap", "cantidad_neta"]);

function ordenarLineasPor(field) {
  if (sortKeyLineas.value === field) {
    sortDirLineas.value = sortDirLineas.value === "asc" ? "desc" : "asc";
  } else {
    sortKeyLineas.value = field;
    sortDirLineas.value = "asc";
  }
  paginaLineas.value = 1;
}

const lineasOrdenadas = computed(() => {
  const lineas = reporteSeleccionado.value?.lineas || [];
  if (!sortKeyLineas.value) return lineas;
  const campo = sortKeyLineas.value;
  const esNumerico = CAMPOS_NUMERICOS_LINEAS.has(campo);
  return [...lineas].sort((a, b) => {
    const cmp = esNumerico
      ? (a[campo] ?? 0) - (b[campo] ?? 0)
      : String(a[campo] ?? "").localeCompare(String(b[campo] ?? ""), "es", {
          sensitivity: "base",
          numeric: true,
        });
    return sortDirLineas.value === "asc" ? cmp : -cmp;
  });
});

const porPaginaLineas = computed(() => paginacionLineasStore.porPagina);
const totalPaginasLineas = computed(() =>
  Math.max(1, Math.ceil(lineasOrdenadas.value.length / porPaginaLineas.value))
);
const lineasPaginadas = computed(() => {
  const inicio = (paginaLineas.value - 1) * porPaginaLineas.value;
  return lineasOrdenadas.value.slice(inicio, inicio + porPaginaLineas.value);
});
const rangoInicioLineas = computed(() =>
  lineasOrdenadas.value.length === 0
    ? 0
    : (paginaLineas.value - 1) * porPaginaLineas.value + 1
);
const rangoFinLineas = computed(() =>
  Math.min(paginaLineas.value * porPaginaLineas.value, lineasOrdenadas.value.length)
);
const paginasVisiblesLineas = computed(() => {
  const total = totalPaginasLineas.value;
  const actual = paginaLineas.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

function cambiarPorPaginaLineas(n) {
  paginacionLineasStore.setPorPagina(n);
  paginaLineas.value = 1;
}

watch(reporteSeleccionadoId, () => {
  paginaLineas.value = 1;
  sortKeyLineas.value = null;
  sortDirLineas.value = "asc";
});

function paramsActuales() {
  const p = { estatus: tabActivo.value };
  if (filtro.value) p.search = filtro.value;
  if (fechaInicio.value && fechaFin.value) {
    p.fecha_inicio = fechaInicio.value;
    p.fecha_fin = fechaFin.value;
  }
  return p;
}

async function cargarReportes() {
  cargando.value = true;
  try {
    reportes.value = await getReportesProduccion(paramsActuales());
  } catch {
    Swal.fire("Error", "No se pudieron cargar los reportes de producción", "error");
  } finally {
    cargando.value = false;
  }
}

async function cargarResumen() {
  try {
    const data = await getResumenReportesProduccion(paramsActuales());
    kpis.value = data.kpis;
    conteos.value = data.conteos_por_estatus;
  } catch {
    // silencioso: los KPIs no son críticos para seguir usando la tabla
  }
}

async function cargarTodo() {
  reporteSeleccionadoId.value = null;
  await Promise.all([cargarReportes(), cargarResumen()]);
}

function cambiarTab(valor) {
  tabActivo.value = valor;
}

watch([filtro, fechaInicio, fechaFin, tabActivo], () => {
  cargarTodo();
});

// ── Validación de Almacén (conteo físico por renglón) ──────────
// Persistido en backend: 'cantidad_recibida', 'diferencia' y
// 'estatus_validacion' vienen directo de la línea (ver
// apps/inyeccion/models.py ReporteProduccionLinea) — cualquiera que entre
// ve exactamente lo mismo, y sobrevive a un refresh.
function diferenciaLinea(l) {
  return l.diferencia;
}
function claseDiferencia(dif) {
  if (dif === 0) return "ra-diferencia--ok";
  return dif > 0 ? "ra-diferencia--sobra" : "ra-diferencia--falta";
}
function claseValidacion(l) {
  switch (l.estatus_validacion) {
    case "COINCIDE":
      return "ra-badge-validacion--coincide";
    case "CON_DIFERENCIA":
      return "ra-badge-validacion--diferencia";
    case "DIFERENCIA_JUSTIFICADA":
      return "ra-badge-validacion--justificada";
    default:
      return "ra-badge-validacion--pendiente";
  }
}
function etiquetaValidacion(l) {
  switch (l.estatus_validacion) {
    case "COINCIDE":
      return "Coincide";
    case "CON_DIFERENCIA":
      return "Con diferencia";
    case "DIFERENCIA_JUSTIFICADA":
      return "Dif. justificada";
    default:
      return "Pendiente";
  }
}

// Borrador libre (mismo patrón que inventario_inicial en CrudProductos):
// se guarda de inmediato en @change, no por tecla. Bloqueado una vez que
// la línea ya tiene una diferencia justificada (ver :disabled abajo).
const guardandoRecibidoId = ref(null);
async function guardarRecibido(l) {
  if (
    l.cantidad_recibida === null ||
    l.cantidad_recibida === "" ||
    l.cantidad_recibida === undefined
  )
    return;
  guardandoRecibidoId.value = l.id;
  try {
    const actualizada = await capturarCantidadRecibida(
      reporteSeleccionado.value.id,
      l.id,
      Number(l.cantidad_recibida)
    );
    Object.assign(l, actualizada);
  } catch (e) {
    const msg = e?.response?.data?.detail;
    Swal.fire(
      "Error",
      Array.isArray(msg)
        ? msg.join(" ")
        : msg || "No se pudo guardar la cantidad recibida",
      "error"
    );
    await cargarReportes();
  } finally {
    guardandoRecibidoId.value = null;
  }
}

// ── Modal "Registrar diferencia de recepción" ──────────────────
const modalDiferenciaElement = ref(null);
let modalDiferenciaInstance = null;
const lineaDiferenciaActual = ref(null);
const formDiferencia = ref({ motivo: "", observaciones: "" });
const guardandoDiferencia = ref(false);
const observacionesRequeridas = computed(() =>
  MOTIVOS_REQUIEREN_OBSERVACIONES.has(formDiferencia.value.motivo)
);

function abrirModalDiferencia(l) {
  lineaDiferenciaActual.value = l;
  formDiferencia.value = { motivo: "", observaciones: "" };
  modalDiferenciaInstance.show();
}

async function guardarDiferencia() {
  const l = lineaDiferenciaActual.value;
  if (!formDiferencia.value.motivo) {
    Swal.fire("Falta información", "Selecciona el motivo de la diferencia", "warning");
    return;
  }
  if (observacionesRequeridas.value && !formDiferencia.value.observaciones.trim()) {
    Swal.fire(
      "Falta información",
      `Las observaciones son obligatorias para el motivo "${formDiferencia.value.motivo}"`,
      "warning"
    );
    return;
  }
  guardandoDiferencia.value = true;
  try {
    const actualizada = await justificarDiferenciaLinea(
      reporteSeleccionado.value.id,
      l.id,
      {
        motivo: formDiferencia.value.motivo,
        observaciones: formDiferencia.value.observaciones,
      }
    );
    Object.assign(l, actualizada);
    // Espera a que el modal termine de cerrarse para que el aviso no
    // aparezca encimado sobre su fade-out.
    await new Promise((resolve) => {
      modalDiferenciaElement.value.addEventListener("hidden.bs.modal", () => resolve(), {
        once: true,
      });
      modalDiferenciaInstance.hide();
    });
    Swal.fire({
      icon: "success",
      title: "Diferencia registrada",
      timer: 1800,
      showConfirmButton: false,
    });
  } catch (e) {
    const msg = e?.response?.data?.detail;
    Swal.fire(
      "Error",
      Array.isArray(msg) ? msg.join(" ") : msg || "No se pudo registrar la diferencia",
      "error"
    );
  } finally {
    guardandoDiferencia.value = false;
  }
}

// ── KPIs de Almacén (ver bloque de tarjetas arriba) ────────────
// Reactivos: con un reporte seleccionado, se calculan solo sobre sus
// líneas; si no, sobre todas las líneas de los reportes ya filtrados
// (fecha de emisión / búsqueda / tab) que llegan en `reportes`.
const lineasParaKpis = computed(() =>
  reporteSeleccionado.value
    ? reporteSeleccionado.value.lineas || []
    : reportes.value.flatMap((r) => r.lineas || [])
);

const kpisAlmacen = computed(() => {
  const lineas = lineasParaKpis.value;
  let reportado = 0;
  let recibido = 0;
  let revisadas = 0;
  for (const l of lineas) {
    reportado += l.cantidad_neta;
    if (l.cantidad_recibida !== null && l.cantidad_recibida !== undefined) {
      revisadas++;
      recibido += l.cantidad_recibida;
    }
  }
  const total = lineas.length;
  return {
    reportado,
    recibido,
    revisadas,
    pendientes: total - revisadas,
    total,
    porcentaje: total ? Math.round((revisadas / total) * 100) : 0,
  };
});

// KPI "Con diferencias": cuenta justificadas y sin justificar (si solo
// contara las pendientes, una diferencia desaparecería del KPI en cuanto se
// justifica, y en "Recepción confirmada" siempre marcaría 0). Sin reporte
// seleccionado cuenta reportes; con uno seleccionado, sus registros.
function tieneDiferencia(l) {
  return l.diferencia !== null && l.diferencia !== undefined && l.diferencia !== 0;
}
function sinJustificar(l) {
  return l.estatus_validacion === "CON_DIFERENCIA";
}
const kpiDiferencias = computed(() => {
  if (reporteSeleccionado.value) {
    const lineas = reporteSeleccionado.value.lineas || [];
    return {
      total: lineas.filter(tieneDiferencia).length,
      sinJustificar: lineas.filter(sinJustificar).length,
    };
  }
  return {
    total: reportes.value.filter((r) => (r.lineas || []).some(tieneDiferencia)).length,
    sinJustificar: reportes.value.filter((r) => (r.lineas || []).some(sinJustificar)).length,
  };
});
const subtituloDiferencias = computed(() => {
  const { total, sinJustificar: pendientes } = kpiDiferencias.value;
  if (!total) return "sin diferencias";
  if (pendientes) return `${pendientes} sin justificar`;
  return "todas justificadas";
});

// RN: no se puede confirmar si queda alguna línea "Con diferencia" sin
// justificar — deben estar todas en Coincide o Diferencia justificada.
const puedeConfirmarRecepcion = computed(() => {
  const lineas = reporteSeleccionado.value?.lineas || [];
  if (!lineas.length) return false;
  return lineas.every(
    (l) =>
      l.estatus_validacion === "COINCIDE" ||
      l.estatus_validacion === "DIFERENCIA_JUSTIFICADA"
  );
});

const confirmandoRecepcion = ref(false);
async function confirmarRecepcion() {
  confirmandoRecepcion.value = true;
  try {
    const actualizado = await confirmarRecepcionReporte(reporteSeleccionado.value.id);
    Object.assign(reporteSeleccionado.value, actualizado);
    await cargarResumen();
    Swal.fire({
      icon: "success",
      title: "Recepción confirmada",
      text: "Las piezas recibidas se sumaron al inventario.",
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (e) {
    const msg = e?.response?.data?.detail;
    Swal.fire(
      "Error",
      Array.isArray(msg) ? msg.join(" ") : msg || "No se pudo confirmar la recepción",
      "error"
    );
  } finally {
    confirmandoRecepcion.value = false;
  }
}

// ── Exportar ───────────────────────────────────────────────
async function exportar() {
  exportando.value = true;
  try {
    await exportarReportesProduccion(paramsActuales());
  } catch {
    Swal.fire("Error", "No se pudo exportar el reporte", "error");
  } finally {
    exportando.value = false;
  }
}

// ── Helpers de formato ────────────────────────────────────────
function formatNum(v) {
  return new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(v ?? 0);
}
function formatFecha(f) {
  if (!f) return "—";
  return new Date(f + "T12:00:00").toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
// Formato corto (dd/mm/yy) para la cabecera del detalle en pantallas
// angostas, donde el formato largo ya no cabe junto al estatus y el botón
// de regresar en un solo renglón.
function formatFechaCorta(f) {
  if (!f) return "—";
  const d = new Date(f + "T12:00:00");
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const anio = String(d.getFullYear()).slice(-2);
  return `${dia}/${mes}/${anio}`;
}

onMounted(() => {
  cargarTodo();
  modalDiferenciaInstance = new Modal(modalDiferenciaElement.value);
  // El modal vive siempre en el DOM y su cuerpo lee reporteSeleccionado;
  // si la línea se queda colgada al cerrarlo, al cambiar de tab (que
  // deselecciona el reporte) el template truena leyendo .fecha de undefined.
  modalDiferenciaElement.value.addEventListener("hidden.bs.modal", () => {
    lineaDiferenciaActual.value = null;
  });
});
</script>

<style scoped>
.rp-root {
  display: flex;
  flex-direction: column;
}
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}

/* ── Ordenamiento y paginación de la tabla de detalle (líneas) —
   mismo diseño que el catálogo de Productos (CrudProductos.vue). ── */
.th-ordenable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}
.th-ordenable:hover {
  color: var(--d-green);
}
.th-ordenable i {
  font-size: 0.7rem;
  color: var(--d-green);
}
.th-ordenable-hint {
  opacity: 0.35;
  transition: opacity 0.15s;
}
.th-ordenable:hover .th-ordenable-hint {
  opacity: 0.8;
}

.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}
.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}
.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}
@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

/* ── Botones toolbar ── */
.btn-accion {
  border-radius: var(--d-radius-pill);
  padding: 7px 16px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s;
}
.btn-accion:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
/* En el rango 992–1199.98px (sidebar expandido + rango de fechas + 3
   botones) el texto de los botones ya no cabe en una sola línea del
   toolbar y lo desborda sobre el panel de KPIs; se compactan a
   solo-ícono igual que en móvil para que todo quepa en un renglón. */
@media (max-width: 1199.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}
.btn-exportar {
  background: var(--d-info, #1565c0);
  border: 1.5px solid var(--d-info, #1565c0);
  color: white;
}
.btn-exportar:hover:not(:disabled) {
  background: #0d47a1;
}

/* ── KPIs ── */
.rp-kpis {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin: 0px 0px 16px;
}
.rp-kpi {
  border-radius: 14px;
  box-shadow: var(--d-elevation-1);
  padding: 16px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.rp-kpi--verde-suave {
  background: #eef1e2;
}
.rp-kpi--verde {
  background: #e5f3e6;
}
.rp-kpi--amarillo {
  background: #fcf2da;
}
.rp-kpi--naranja {
  background: #fbe8da;
}
.rp-kpi--azul {
  background: #e2eef9;
}
.rp-kpi-icono {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #fff;
  flex-shrink: 0;
}
.rp-kpi--verde-suave .rp-kpi-icono {
  background: var(--d-green);
}
.rp-kpi--verde .rp-kpi-icono {
  background: #2e7d32;
}
.rp-kpi--amarillo .rp-kpi-icono {
  background: #d48804;
}
.rp-kpi--naranja .rp-kpi-icono {
  background: #e65100;
}
.rp-kpi--azul .rp-kpi-icono {
  background: #1565c0;
}
.rp-kpi-texto {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.rp-kpi-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--d-brown);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.rp-kpi-valor {
  display: block;
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--d-brown);
  white-space: nowrap;
}
.rp-kpi-valor--chico {
  font-size: 0.85rem;
}
.rp-kpi-unidad {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--d-brown);
  opacity: 0.55;
}
.rp-kpi-sub {
  display: block;
  font-size: 0.76rem;
  font-weight: 500;
  color: var(--d-brown);
  opacity: 0.55;
  white-space: nowrap;
}

/* ── Tabs ── */
.rp-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  padding: 10px 16px;
  background: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-1);
  margin-bottom: 14px;
}
.rp-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 3px 2px;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--d-brown);
  opacity: 0.55;
  cursor: pointer;
  transition: all 0.15s;
}
.rp-tab:hover {
  opacity: 0.85;
}
.rp-tab.activo {
  opacity: 1;
  color: var(--d-green-dark);
  border-bottom-color: var(--d-green);
}
.rp-tab-icono {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 7px;
  font-size: 0.8rem;
  flex-shrink: 0;
}
.rp-tab-icono--pendiente {
  background: rgba(212, 136, 4, 0.16);
  color: #d48804;
}
.rp-tab-icono--enviado {
  background: rgba(21, 101, 192, 0.16);
  color: #1565c0;
}
.rp-tab-icono--completado {
  background: rgba(46, 125, 50, 0.16);
  color: #2e7d32;
}
.rp-tab-icono--cancelado {
  background: rgba(192, 57, 43, 0.16);
  color: #c0392b;
}

/* ── Barra de selección masiva ── */
.rp-barra-seleccion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--d-cream);
  border: 1px solid rgba(96, 108, 56, 0.25);
  border-radius: 10px;
  padding: 8px 14px;
  margin-bottom: 12px;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--d-green-dark);
}
.btn-limpiar-seleccion {
  background: none;
  border: 1px solid rgba(64, 34, 8, 0.3);
  color: var(--d-brown);
  border-radius: var(--d-radius-pill);
  padding: 4px 12px;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}
.btn-enviar-aprobacion {
  background: var(--d-green);
  border: none;
  color: white;
  border-radius: var(--d-radius-pill);
  padding: 5px 14px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}
.btn-enviar-aprobacion:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ── Tabla ── */
.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-2);
  overflow: hidden;
}
.table-scroll {
  overflow-x: auto;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 1px solid var(--d-border);
}
.table-divisan th {
  padding: 12px 15px;
  font-weight: 800;
  font-size: 0.74rem;
  text-transform: uppercase;
  border: none;
  white-space: nowrap;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 10px 15px;
  white-space: nowrap;
  font-size: 0.85rem;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.05);
}
.rp-fila-clicable {
  cursor: pointer;
}
.rp-fila-totales td {
  font-weight: 800;
  color: var(--d-brown);
  background: rgba(96, 108, 56, 0.06);
  border-top: 2px solid rgba(96, 108, 56, 0.2);
}
.rp-checkbox {
  width: 16px;
  height: 16px;
  accent-color: var(--d-green);
  cursor: pointer;
}

/* ── Cards móvil (vista resumen) ── */
.rp-card {
  position: relative;
  background: white;
  border-radius: 12px;
  border-left: 5px solid rgba(96, 108, 56, 0.3);
  box-shadow: var(--d-elevation-1);
  padding: 12px 14px;
  margin: 10px 12px;
  cursor: pointer;
}
.rp-card.badge-estatus-rp--pendiente {
  border-left-color: #d48804;
}
.rp-card.badge-estatus-rp--enviado {
  border-left-color: #1565c0;
}
.rp-card.badge-estatus-rp--completado {
  border-left-color: #2e7d32;
}
.rp-card.badge-estatus-rp--cancelado {
  border-left-color: #c0392b;
}
.rp-card-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  padding-right: 42px;
}
.rp-card-no {
  position: absolute;
  top: 10px;
  right: 12px;
  font-size: 0.68rem;
  font-weight: 800;
  color: white;
  background: var(--d-green);
  padding: 2px 9px;
  border-radius: 20px;
  line-height: 1.5;
}
.rp-card-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.rp-card-fecha {
  font-size: 0.78rem;
  color: var(--d-brown);
  opacity: 0.7;
}
.rp-card-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  padding: 8px 0;
  border-top: 1px dashed rgba(96, 108, 56, 0.2);
  border-bottom: 1px dashed rgba(96, 108, 56, 0.2);
  margin-bottom: 8px;
}
.rp-card-stats div {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.rp-card-stats small {
  font-size: 0.62rem;
  color: var(--d-brown);
  opacity: 0.6;
  text-transform: uppercase;
  font-weight: 700;
}
.rp-card-stats strong {
  font-size: 0.85rem;
  color: var(--d-brown);
}
.rp-card-acciones {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* ── Cards móvil de la tabla de detalle (renglones) ── */
.rp-linea-card-top {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rp-linea-card-info {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 6px 0;
  font-size: 0.78rem;
  color: var(--d-brown);
  opacity: 0.85;
}
.rp-linea-card-info i {
  color: var(--d-green);
  opacity: 0.8;
}
.rp-linea-card-stats {
  grid-template-columns: repeat(3, 1fr);
}
.ra-linea-card-estatus {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
}
.ra-linea-card-validacion {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding-top: 8px;
  margin-top: 8px;
  border-top: 1px dashed rgba(96, 108, 56, 0.2);
}
.ra-linea-card-almacen,
.ra-linea-card-diferencia {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 0;
  min-width: 0;
}
.ra-linea-card-almacen label,
.ra-linea-card-diferencia label {
  font-size: 0.62rem;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--d-brown);
  opacity: 0.6;
}
.ra-linea-card-almacen .ra-input-almacen {
  width: 100%;
  margin: 0;
  text-align: left;
  padding-left: 8px;
}

.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
}
.btn-circular:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-circular:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* ── Badges estatus / aditivo ── */
.badge-estatus-rp {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}
.badge-estatus-rp--pendiente {
  background: #fff3cd;
  color: #7a5a00;
}
.badge-estatus-rp--enviado {
  background: #cfe2ff;
  color: #1565c0;
}
.badge-estatus-rp--completado {
  background: #d1e7dd;
  color: #0f5132;
}
.badge-estatus-rp--cancelado {
  background: #f8d7da;
  color: #842029;
}
.badge-aditivo-rp {
  display: inline-flex;
  min-width: 34px;
  justify-content: center;
  padding: 2px 6px;
  border-radius: 6px;
  background: rgba(64, 34, 8, 0.08);
  color: var(--d-brown);
  opacity: 0.6;
  font-size: 0.72rem;
  font-weight: 800;
}
.badge-aditivo-rp.activo {
  background: rgba(46, 125, 50, 0.14);
  color: #2e7d32;
  opacity: 1;
}

/* ── Validación de Almacén: conteo físico, diferencia y estatus ── */
.ra-input-almacen {
  width: 90px;
  margin: 0 auto;
  text-align: center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
}
.ra-input-almacen:disabled {
  background-color: rgba(96, 108, 56, 0.08);
  opacity: 0.8;
}
.ra-input-almacen.guardando {
  border-color: var(--d-info, #1565c0);
  opacity: 0.7;
}
.ra-diferencia-vacia {
  color: var(--d-brown);
  opacity: 0.4;
}
.ra-diferencia {
  font-weight: 800;
}
.ra-diferencia--ok {
  color: #2e7d32;
}
.ra-diferencia--sobra {
  color: #1565c0;
}
.ra-diferencia--falta {
  color: #c0392b;
}
.ra-badge-validacion {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}
.ra-badge-validacion--pendiente {
  background: #fff3cd;
  color: #7a5a00;
}
.ra-badge-validacion--coincide {
  background: #d1e7dd;
  color: #0f5132;
}
.ra-badge-validacion--diferencia {
  background: #f8d7da;
  color: #842029;
}
.ra-badge-validacion--justificada {
  background: rgba(96, 108, 56, 0.16);
  color: var(--d-green-dark);
}

/* ── Acciones: botón "Registrar diferencia" por renglón ──
   Solo existe para Con diferencia (activo) y Diferencia justificada
   (bloqueado, ya no se puede reabrir). Pendiente/Coincide no llevan botón
   — no hay nada que resumir. ── */
.btn-accion-diferencia {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1.5px solid #e65100;
  background: #fff3e8;
  color: #e65100;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-accion-diferencia:hover:not(:disabled) {
  background: #ffe3c9;
}
.btn-accion-diferencia--bloqueado {
  border-color: rgba(96, 108, 56, 0.35);
  background: rgba(96, 108, 56, 0.08);
  color: var(--d-green);
  cursor: not-allowed;
}

/* ── Vista detalle: columnas Operador/Estatus pueden partirse a dos
   renglones en vez de forzar el ancho de toda la tabla ── */
.rp-th-wrap {
  white-space: normal !important;
  text-align: left;
}
.rp-celda-wrap {
  white-space: normal !important;
  text-align: left;
  line-height: 1.25;
}
.rp-celda-wrap .badge-estatus-rp {
  white-space: normal;
  text-align: left;
  line-height: 1.2;
}
.rp-prod-modelo-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  margin-top: 2px;
}
.rp-color-swatch-rect {
  display: inline-block;
  width: 28px;
  height: 14px;
  border-radius: 4px;
  border: 1.5px solid rgba(64, 34, 8, 0.15);
  flex-shrink: 0;
}
.rp-color-swatch {
  display: inline-block;
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 1.5px solid rgba(64, 34, 8, 0.15);
  vertical-align: middle;
  margin-right: 8px;
}

/* ── Vista detalle ── */
.rp-detalle-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(96, 108, 56, 0.15);
  flex-wrap: wrap;
}
.btn-regresar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  background: var(--d-brown);
  color: var(--d-cream);
  border: none;
  border-radius: 50%;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.18s;
}
.btn-regresar:hover {
  background: #703808;
}
.rp-detalle-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--d-brown);
  font-size: 0.88rem;
}
.rp-detalle-sep {
  opacity: 0.4;
}
.rp-fecha-corta,
.rp-estatus-corto {
  display: none;
}
/* ── Cabecera del detalle en pantallas angostas: fecha corta (dd/mm/yy),
   estatus corto y sin el nombre del encargado (ya se muestra en la tabla
   resumen) — así el botón de regresar, la fecha y el estatus caben en un
   solo renglón. ── */
@media (max-width: 425px) {
  .rp-fecha-larga,
  .rp-estatus-largo {
    display: none;
  }
  .rp-fecha-corta,
  .rp-estatus-corto {
    display: inline;
  }
  .rp-detalle-encargado {
    display: none;
  }
}
.btn-confirmar-recepcion {
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  background: var(--d-green);
  color: white;
  border: none;
  border-radius: var(--d-radius-pill);
  padding: 7px 16px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s;
}
.btn-confirmar-recepcion:hover:not(:disabled) {
  background: var(--d-green-dark);
}
.btn-confirmar-recepcion:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Toolbar en tablet (576–991.98px): buscador, fechas y botones caben
   en un solo renglón — el buscador crece para llenar el espacio libre
   en vez de quedar angosto con espacio suelto a la derecha. ── */
@media (min-width: 576px) and (max-width: 991.98px) {
  :deep(.crud-toolbar) {
    gap: 8px !important;
  }
  :deep(.crud-toolbar-center) {
    order: 1 !important;
    flex: 1 1 160px !important;
    justify-content: flex-start !important;
    min-width: 0 !important;
  }
  :deep(.crud-toolbar-search) {
    max-width: none !important;
  }
  :deep(.crud-toolbar-extra) {
    order: 2 !important;
    flex: 0 0 auto !important;
  }
  :deep(.crud-toolbar-actions) {
    order: 3 !important;
    gap: 6px !important;
    flex-shrink: 0 !important;
  }
}

/* ── Toolbar en móvil (<576px): ya no caben en un renglón — buscador +
   botones arriba, rango de fechas abajo — pero ambos renglones usan
   todo el ancho disponible en vez de quedar angostos. ── */
@media (max-width: 575.98px) {
  :deep(.crud-toolbar) {
    gap: 8px !important;
    flex-wrap: wrap !important;
  }
  :deep(.crud-toolbar-center) {
    order: 1 !important;
    /* flex-basis en 0 (en vez de "auto") para que el ancho natural del
       input de búsqueda no cuente al decidir si los botones caben en el
       mismo renglón — así el buscador parte de 0 y el flex-grow le da
       justo el espacio que sobra junto a los botones. */
    flex: 1 1 0 !important;
    justify-content: flex-start !important;
    min-width: 0 !important;
  }
  :deep(.crud-toolbar-search) {
    max-width: none !important;
  }
  :deep(.crud-toolbar-actions) {
    order: 1 !important;
    margin-left: auto !important;
    gap: 6px !important;
    flex-shrink: 0 !important;
  }
  :deep(.crud-toolbar-extra) {
    order: 2 !important;
    flex: 1 1 100% !important;
    width: 100% !important;
  }
  :deep(.rango-fechas) {
    width: 100%;
  }
  :deep(.fecha-input) {
    flex: 1 1 0;
    min-width: 0;
  }
}
@media (max-width: 360px) {
  :deep(.btn-header) {
    width: 30px !important;
    height: 30px !important;
    font-size: 0.78rem !important;
  }
  /* A esta medida la tarjeta ya no tiene espacio para "pzas" junto al
     número y lo empuja fuera del recuadro — se quita solo aquí. */
  .rp-kpi-unidad {
    display: none;
  }
}

/* ── KPIs en tablet: 2 columnas para que quepan etiqueta + valor ──
   Sube hasta 1024px: el modo responsive de esta pantalla arranca ahí
   (ver .rp-vista-desktop/.rp-vista-movil más abajo), no en el lg de
   Bootstrap (992px). ── */
@media (max-width: 1024px) and (min-width: 576px) {
  .rp-kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/* ── Tabla de escritorio vs. cards móvil: el quiebre es 1024px (no el lg
   de Bootstrap/992px). ── */
.rp-vista-desktop {
  display: none;
}
.rp-vista-movil {
  display: block;
}
@media (min-width: 1025px) {
  .rp-vista-desktop {
    display: block;
  }
  .rp-vista-movil {
    display: none;
  }
}

/* ── KPIs en móvil: solo ícono + número, los 5 en un renglón ── */
@media (max-width: 575.98px) {
  .rp-kpi {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 10px 4px;
    text-align: center;
  }
  .rp-kpi-icono {
    width: 30px;
    height: 30px;
    font-size: 0.9rem;
  }
  .rp-kpi-texto {
    align-items: center;
  }
  .rp-kpi-label,
  .rp-kpi-sub {
    display: none;
  }
  .rp-kpi-valor {
    font-size: 0.85rem;
    white-space: nowrap;
  }

  /* ── Tabs en móvil: pastilla coloreada con ícono + contador en fila ── */
  .rp-tabs {
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: 6px;
    padding: 8px 10px;
  }
  .rp-tab {
    flex: 1 1 0;
    flex-direction: row;
    justify-content: center;
    gap: 5px;
    border-bottom: none;
    border-radius: 999px;
    opacity: 1;
  }
  .rp-tab-icono {
    background: transparent !important;
    width: auto;
    height: auto;
    font-size: 0.95rem;
  }
  .rp-tab--pendiente {
    background: rgba(212, 136, 4, 0.14);
    color: #d48804;
  }
  .rp-tab--enviado {
    background: rgba(21, 101, 192, 0.14);
    color: #1565c0;
  }
  .rp-tab--completado {
    background: rgba(46, 125, 50, 0.14);
    color: #2e7d32;
  }
  .rp-tab--cancelado {
    background: rgba(192, 57, 43, 0.14);
    color: #c0392b;
  }
  .rp-tab.activo {
    box-shadow: inset 0 0 0 1.5px currentColor;
  }
  .rp-tab-label {
    display: none;
  }
  .rp-tab-count {
    font-size: 0.78rem;
    font-weight: 800;
    color: inherit;
  }
}

/* ── Modal "Registrar diferencia de recepción" ───────────────
   Mismo look estándar de modal que el resto del proyecto (ver
   CrudProductos.vue): fondo crema, borde café, título simple sin banda de
   color — en vez del diseño naranja/aislado que tenía antes. ── */
.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}
.divisan-input-modal:disabled {
  background-color: rgba(96, 108, 56, 0.08);
  opacity: 0.8;
}
.rd-textarea-observaciones {
  min-height: 38px;
  height: 38px;
  resize: vertical;
}
.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: #703808;
  color: var(--d-cream);
}
.btn-nuevo:disabled {
  opacity: 0.6;
}
.btn-cancelar-ajuste {
  background: white;
  border: 1.5px solid var(--d-brown);
  color: var(--d-brown);
  border-radius: 8px;
  padding: 0.5rem 1.2rem;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-cancelar-ajuste:hover:not(:disabled) {
  background: rgba(64, 34, 8, 0.06);
}
.btn-cancelar-ajuste:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Un poco más ancho en escritorio para que quepa bien la grilla de
   detalle; en móvil conserva el ancho responsivo normal de Bootstrap. */
@media (min-width: 768px) {
  .rd-modal-dialog {
    max-width: 640px;
  }
}

.rd-subtitulo {
  font-size: 0.86rem;
  margin-bottom: 14px;
}
.rd-info-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 4px;
}
.rd-info-box {
  display: grid;
  gap: 12px 16px;
  background: rgba(96, 108, 56, 0.05);
  border: 1.5px solid var(--d-green);
  border-radius: 10px;
  padding: 10px 14px;
}
.rd-info-row--2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.rd-info-row--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.rd-info-box > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.rd-info-box small {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--d-brown);
  opacity: 0.6;
}
.rd-info-box strong {
  color: var(--d-brown);
  font-size: 0.9rem;
  white-space: normal;
  word-break: break-word;
  line-height: 1.25;
}
.rd-diferencia-valor.ra-diferencia--sobra {
  color: #1565c0;
}
.rd-diferencia-valor.ra-diferencia--falta {
  color: #c0392b;
}
.rd-contador {
  display: block;
  text-align: right;
  color: var(--d-brown);
  opacity: 0.55;
  font-size: 0.72rem;
  margin-top: 2px;
}
.rd-nota {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px dashed rgba(96, 108, 56, 0.25);
  font-size: 0.78rem;
  color: var(--d-green-dark);
  line-height: 1.35;
}
.rd-nota i {
  flex-shrink: 0;
  margin-top: 1px;
}
@media (max-width: 575.98px) {
  .rd-info-row--2,
  .rd-info-row--3 {
    grid-template-columns: 1fr;
  }
}
</style>
